import { useContext, useEffect, useState } from 'react';

import {
	Button,
	Col,
	Divider,
	Drawer,
	Flex,
	Form,
	Input,
	Select,
	UploadFile,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import { SubscriptionContext } from '@/App';
import { useCreateBatchProcess } from '@/api/batch_process';
import FileUpload from '@/components/common/FileUploader';
import { WHISPER_ACTIONS } from '@/const';
import { PageContext } from '@/providers/PageContextProvider';

const layout = {
	labelCol: { span: 5 },
	wrapperCol: { span: 21 },
};

function isValidUrl(url: string): boolean {
	try {
		new URL(url);
		return true;
	} catch {
		return false;
	}
}

export default function AddNewBatchProcessDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);
	const { subscriptions } = useContext(SubscriptionContext);
	const [content, setContent] = useState<string>('');
	const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);

	const [form] = useForm();

	const vodSubscription = subscriptions?.filter(
		(sub) => sub?.type?.toString() === 'vod' && sub.active,
	)?.[0];

	const { mutate, isLoading } = useCreateBatchProcess();

	const onCancelClick = () => {
		form.resetFields();
		closeDrawer();
	};

	const onSaveClick = async () => {
		if (uploadedFile && content) {
			try {
				await form.validateFields(['actions', 'tags']);
			} catch (error) {
				console.log('error', error);
				return;
			}

			const urls = content
				.split('\n')
				.map((item) => item.trim())
				.filter((item) => item !== '' && item !== '\n');

			if (urls.length === 0) {
				message.error(
					'The uploaded file is empty or contains only empty lines.',
				);
				return;
			}

			// check if valid urls
			for (const url of urls) {
				if (!isValidUrl(url)) {
					message.error(`Invalid URL: ${url}`);
					return;
				}
			}

			const { actions, tags, name } = form.getFieldsValue();

			mutate(
				{
					actions,
					tags,
					urls: urls,
					name: name ? name : '',
				},
				{
					onSuccess: () => {
						form.resetFields();
						setUploadedFile(null);
						setContent('');
						message.success({ content: 'Sent' });
						closeDrawer();
					},
					onError: (error: any) => {
						message.error({
							type: 'error',
							content:
								error?.response?.data?.message ?? error?.message ?? 'Error',
						});
					},
				},
			);
		} else {
			try {
				await form.validateFields();

				const { actions, tags, urls, name } = form.getFieldsValue();

				mutate(
					{
						actions,
						tags,
						urls: urls?.split('\n').map((item: string) => item && item?.trim()),
						name: name ? name : '',
					},
					{
						onSuccess: () => {
							message.success({ content: 'Added' });
							form.resetFields();
							closeDrawer();
						},
						onError: (error: any) => {
							message.error({
								type: 'error',
								content:
									error?.response?.data?.message ?? error?.message ?? 'Error',
							});
						},
					},
				);
			} catch (error) {
				console.log(error);
				// message.error('Please fix the errors in the form before submitting.');
			}
		}
	};

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'add-new-batch-process'}
			width={'650px'}
			onClose={onCancelClick}
			title="Video on-demand - new batch"
		>
			<Col span={24}>
				<Flex
					justify="end"
					align="center"
					gap={20}
					style={{ marginBottom: '1.25rem' }}
				>
					<Button onClick={onCancelClick}>Cancel</Button>
					<Button type="primary" onClick={onSaveClick} loading={isLoading}>
						Save
					</Button>
				</Flex>
			</Col>
			<Form form={form} {...layout}>
				<Form.Item label="Name" name="name">
					<Input />
				</Form.Item>

				{/* Tags */}
				<Form.Item
					label="Tags"
					name="tags"
					rules={[
						{
							required: true,
							message: 'Tags are required. Add at least one.',
						},
					]}
				>
					<Select
						mode="tags"
						style={{ width: '100%' }}
						placeholder="Add tags"
						options={[]}
					/>
				</Form.Item>
				{/* Actions */}

				<Form.Item
					label="Actions"
					name="actions"
					rules={[
						{
							required: true,
							message: 'Actions are required. Choose at least one.',
						},
					]}
				>
					<Select placeholder="Select actions" allowClear mode="multiple">
						{WHISPER_ACTIONS?.filter((item) =>
							vodSubscription?.visible_actions?.includes(item?.value),
						).map((item) => (
							<Select.Option
								value={item.value}
								key={item.value}
								disabled={
									vodSubscription?.actions?.includes(item?.value) === false
								}
							>
								{item.label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				{/* URLs to analyse */}
				<Form.Item
					label="URLs to analyse"
					name="urls"
					style={{ marginBottom: '0.75rem' }}
					rules={[
						{
							required: true,
							message: 'URLs are required',
						},
						{
							whitespace: true,
							message: 'At least one URL is required',
						},
					]}
				>
					<TextArea
						rows={12}
						placeholder={`https://www.youtube.com/watch?v=jNQXAC9IVRw\nhttps://www.instagram.com/p/C-GFnTrJSNi/\nhttps://www.tiktok.com/@look556666/video/7184420791466118446`}
					/>
				</Form.Item>

				<Flex vertical style={{ marginLeft: '128.75px' }}>
					<Divider plain style={{ color: 'gray', marginTop: 0 }}>
						OR
					</Divider>

					<FileUpload
						uploadedFile={uploadedFile}
						setUploadedFile={setUploadedFile}
						setContent={setContent}
					/>
				</Flex>
			</Form>
		</Drawer>
	);
}
